import React, { useState } from 'react';
import { Check, X, RotateCcw } from 'lucide-react';

export interface BaseInteractiveProps {
  exercise: any;
  onAnswer: (answer: any) => void;
  showFeedback?: boolean;
  disabled?: boolean;
}

// Quiz Question Renderer - handles different question types within a quiz
export const QuizQuestionRenderer: React.FC<BaseInteractiveProps> = ({
  exercise,
  onAnswer,
  showFeedback = false,
  disabled = false
}) => {
  // Handle different question types
  if (exercise.type === 'true_false') {
    return (
      <TrueFalseRenderer
        exercise={exercise}
        onAnswer={onAnswer}
        showFeedback={showFeedback}
        disabled={disabled}
      />
    );
  }

  if (exercise.type === 'fill_in_the_blank') {
    return (
      <FillInBlankRenderer
        exercise={exercise}
        onAnswer={onAnswer}
        showFeedback={showFeedback}
        disabled={disabled}
      />
    );
  }

  if (exercise.type === 'matching') {
    return (
      <MatchingRenderer
        exercise={exercise}
        onAnswer={onAnswer}
        showFeedback={showFeedback}
        disabled={disabled}
      />
    );
  }

  // Default to multiple choice for any other type or if no type specified
  return (
    <MultipleChoiceComponent
      exercise={exercise}
      onAnswer={onAnswer}
      showFeedback={showFeedback}
      disabled={disabled}
    />
  );
};

// True/False Question Component
const TrueFalseRenderer: React.FC<BaseInteractiveProps> = ({
  exercise,
  onAnswer,
  showFeedback = false,
  disabled = false
}) => {
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [hasAnswered, setHasAnswered] = useState(false);

  const handleSelect = (index: number) => {
    if (disabled || hasAnswered) return;
    setSelectedAnswer(index);
    setHasAnswered(true);
    onAnswer(index);
  };

  return (
    <div className="space-y-4">
      <div className="mb-4">
        <p className="text-gray-700 mb-3">{exercise.content}</p>
        <p className="font-medium text-gray-900 mb-3">{exercise.question}</p>
      </div>

      <div className="space-y-2">
        {['True', 'False'].map((option: string, index: number) => (
          <label
            key={index}
            className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
              selectedAnswer === index
                ? showFeedback
                  ? exercise.answer === index
                    ? 'bg-green-50 border-green-300'
                    : 'bg-red-50 border-red-300'
                  : 'bg-blue-50 border-blue-300'
                : 'border-gray-200 hover:bg-gray-50'
            } ${disabled || hasAnswered ? 'cursor-not-allowed opacity-60' : ''}`}
          >
            <input
              type="radio"
              name={`exercise-${exercise.id || 'default'}`}
              checked={selectedAnswer === index}
              onChange={() => handleSelect(index)}
              disabled={disabled || hasAnswered}
              className="text-blue-600"
            />
            <span className="text-gray-700 flex-1">{option}</span>
            {showFeedback && selectedAnswer === index && (
              <span
                className={`text-sm font-medium ${
                  exercise.answer === index ? 'text-green-600' : 'text-red-600'
                }`}
              >
                {exercise.answer === index ? 'Correct' : 'Incorrect'}
              </span>
            )}
          </label>
        ))}
      </div>

      {showFeedback && hasAnswered && exercise.explanation && (
        <div
          className={`p-4 rounded-lg border ${
            exercise.answer === selectedAnswer
              ? 'bg-green-50 border-green-200'
              : 'bg-red-50 border-red-200'
          }`}
        >
          <div className="flex items-start gap-2">
            {exercise.answer === selectedAnswer ? (
              <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
            ) : (
              <X className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
            )}
            <div>
              <p
                className={`text-sm font-medium mb-1 ${
                  exercise.answer === selectedAnswer ? 'text-green-800' : 'text-red-800'
                }`}
              >
                Explanation:
              </p>
              <p
                className={`text-sm ${
                  exercise.answer === selectedAnswer ? 'text-green-700' : 'text-red-700'
                }`}
              >
                {exercise.explanation}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Fill in the Blank Component
const FillInBlankRenderer: React.FC<BaseInteractiveProps> = ({
  exercise,
  onAnswer,
  showFeedback = false,
  disabled = false
}) => {
  const [textInputAnswer, setTextInputAnswer] = useState('');
  const [hasSubmittedText, setHasSubmittedText] = useState(false);

  const handleSubmit = () => {
    if (disabled || hasSubmittedText || !textInputAnswer.trim()) return;
    setHasSubmittedText(true);
    onAnswer(textInputAnswer.trim().toLowerCase());
  };

  return (
    <div className="space-y-4">
      <div className="mb-4">
        <p className="text-gray-700 mb-3">{exercise.content}</p>
        <p className="font-medium text-gray-900 mb-3">{exercise.question}</p>
      </div>

      <div className="space-y-3">
        <input
          type="text"
          value={textInputAnswer}
          onChange={(e) => setTextInputAnswer(e.target.value)}
          disabled={disabled || hasSubmittedText}
          placeholder="Type your answer..."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />

        {!hasSubmittedText && (
          <button
            onClick={handleSubmit}
            disabled={disabled || !textInputAnswer.trim()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Submit Answer
          </button>
        )}
      </div>

      {showFeedback && hasSubmittedText && exercise.explanation && (
        <div
          className={`p-4 rounded-lg border ${
            textInputAnswer.toLowerCase().trim() === exercise.answer?.toLowerCase()
              ? 'bg-green-50 border-green-200'
              : 'bg-red-50 border-red-200'
          }`}
        >
          <div className="flex items-start gap-2">
            {textInputAnswer.toLowerCase().trim() === exercise.answer?.toLowerCase() ? (
              <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
            ) : (
              <X className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
            )}
            <div>
              <p
                className={`text-sm font-medium mb-1 ${
                  textInputAnswer.toLowerCase().trim() === exercise.answer?.toLowerCase()
                    ? 'text-green-800'
                    : 'text-red-800'
                }`}
              >
                {textInputAnswer.toLowerCase().trim() === exercise.answer?.toLowerCase()
                  ? 'Correct!'
                  : 'Incorrect'}
              </p>
              <p
                className={`text-sm ${
                  textInputAnswer.toLowerCase().trim() === exercise.answer?.toLowerCase()
                    ? 'text-green-700'
                    : 'text-red-700'
                }`}
              >
                {exercise.explanation}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Matching Component
const MatchingRenderer: React.FC<BaseInteractiveProps> = ({
  exercise,
  onAnswer,
  showFeedback = false,
  disabled = false
}) => {
  const [matchedPairs, setMatchedPairs] = useState<Record<string, string>>({});
  const [draggedTerm, setDraggedTerm] = useState<string | null>(null);
  const [hasAnswered, setHasAnswered] = useState(false);

  const terms = Object.keys(exercise.pairs || {});
  const definitions = Object.values(exercise.pairs || {});
  const correctPairs = exercise.pairs || {};

  const handleDragStart = (term: string) => {
    if (disabled) return;
    setDraggedTerm(term);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent, definition: string) => {
    e.preventDefault();
    if (disabled || hasAnswered || !draggedTerm) return;

    // Don't allow dropping on the same definition if already matched
    if (matchedPairs[draggedTerm] === definition) return;

    setMatchedPairs((prev) => ({
      ...prev,
      [draggedTerm]: definition
    }));

    setDraggedTerm(null);
  };

  const handleSubmit = () => {
    if (disabled || hasAnswered) return;
    setHasAnswered(true);
    // Create a mapping of term -> definition for answer submission
    const answerMap: Record<string, string> = {};
    Object.entries(matchedPairs).forEach(([term, definition]) => {
      answerMap[term] = definition;
    });
    onAnswer(answerMap);
  };

  const handleReset = () => {
    if (disabled || hasAnswered) return;
    setMatchedPairs({});
    setDraggedTerm(null);
  };

  // Check if all terms are matched
  const allMatched = terms.length > 0 && terms.every((term) => matchedPairs[term]);
  const isCorrect = hasAnswered && terms.every((term) => matchedPairs[term] === correctPairs[term]);

  return (
    <div className="space-y-4">
      <div className="mb-4">
        <p className="text-gray-700 mb-3">{exercise.content}</p>
        <p className="font-medium text-gray-900 mb-3">{exercise.question}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Terms Column - Draggable Items */}
        <div className="space-y-3">
          <h4 className="font-medium text-gray-900 mb-3">Terms</h4>
          <div className="space-y-2">
            {terms.map((term, index) => {
              const isMatched = matchedPairs[term];
              const isCorrectMatch = isCorrect && matchedPairs[term] === correctPairs[term];

              return (
                <div
                  key={index}
                  draggable={!disabled && !isMatched}
                  onDragStart={() => handleDragStart(term)}
                  className={`p-3 rounded-lg border-2 transition-all cursor-pointer ${
                    isMatched
                      ? isCorrectMatch
                        ? 'bg-green-50 border-green-300 text-green-800'
                        : 'bg-red-50 border-red-300 text-red-800'
                      : 'bg-blue-50 border-blue-200 text-blue-800 hover:bg-blue-100'
                  } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{term}</span>
                    {isMatched && (
                      <span
                        className={`text-xs px-2 py-1 rounded-full ${
                          isCorrectMatch ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'
                        }`}
                      >
                        {isCorrectMatch ? '✓' : '✗'}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Definitions Column - Drop Zones */}
        <div className="space-y-3">
          <h4 className="font-medium text-gray-900 mb-3">Definitions</h4>
          <div className="space-y-2">
            {definitions.map((definition, index) => {
              const matchedTerm = Object.keys(matchedPairs).find(
                (term) => matchedPairs[term] === definition
              );
              const isCorrectMatch = matchedTerm && correctPairs[matchedTerm] === definition;

              return (
                <div
                  key={index}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, definition as string)}
                  className={`min-h-16 p-3 rounded-lg border-2 transition-all ${
                    matchedTerm
                      ? isCorrectMatch
                        ? 'bg-green-50 border-green-300'
                        : 'bg-red-50 border-red-300'
                      : 'bg-gray-50 border-gray-200 border-dashed'
                  } ${disabled ? 'cursor-not-allowed' : 'cursor-pointer hover:bg-gray-100'}`}
                >
                  <div className="flex items-start justify-between h-full">
                    <span
                      className={`text-sm ${matchedTerm ? 'text-gray-900' : 'text-gray-500 italic'}`}
                    >
                      {matchedTerm ? (
                        <span className="font-medium">{matchedTerm}</span>
                      ) : (
                        'Drop term here'
                      )}
                    </span>
                    <span className="text-xs text-gray-400">{definition as string}</span>
                  </div>
                  {matchedTerm && (
                    <div className="mt-2 text-xs text-gray-600 bg-white bg-opacity-50 p-1 rounded">
                      {definition as string}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-center gap-3">
        {!hasAnswered && Object.keys(matchedPairs).length > 0 && (
          <button
            onClick={handleReset}
            disabled={disabled}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center gap-2"
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </button>
        )}

        {!hasAnswered && allMatched && (
          <button
            onClick={handleSubmit}
            disabled={disabled}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            Submit Matching
          </button>
        )}
      </div>

      {/* Feedback */}
      {showFeedback && hasAnswered && exercise.explanation && (
        <div
          className={`p-4 rounded-lg border ${
            isCorrect ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
          }`}
        >
          <div className="flex items-start gap-2">
            {isCorrect ? (
              <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
            ) : (
              <X className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
            )}
            <div>
              <p
                className={`text-sm font-medium mb-1 ${
                  isCorrect ? 'text-green-800' : 'text-red-800'
                }`}
              >
                {isCorrect ? 'Correct!' : 'Incorrect'}
              </p>
              <p className={`text-sm ${isCorrect ? 'text-green-700' : 'text-red-700'}`}>
                {exercise.explanation}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Multiple Choice Component (for quiz questions)
const MultipleChoiceComponent: React.FC<BaseInteractiveProps> = ({
  exercise,
  onAnswer,
  showFeedback = false,
  disabled = false
}) => {
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);

  const handleSelect = (index: number) => {
    if (disabled) return;
    setSelectedAnswer(index);
    onAnswer(index);
  };

  return (
    <div className="space-y-4">
      <div className="mb-4">
        <p className="text-gray-700 mb-3">{exercise.content}</p>
        <p className="font-medium text-gray-900 mb-3">{exercise.question}</p>
      </div>

      <div className="space-y-2">
        {exercise.options?.map((option: string, index: number) => (
          <label
            key={index}
            className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
              selectedAnswer === index
                ? showFeedback
                  ? exercise.correctAnswer === index
                    ? 'bg-green-50 border-green-300'
                    : 'bg-red-50 border-red-300'
                  : 'bg-blue-50 border-blue-300'
                : 'border-gray-200 hover:bg-gray-50'
            } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
          >
            <input
              type="radio"
              name={`exercise-${exercise.id || 'default'}`}
              checked={selectedAnswer === index}
              onChange={() => handleSelect(index)}
              disabled={disabled}
              className="text-blue-600"
            />
            <span className="text-gray-700 flex-1">{option}</span>
            {showFeedback && selectedAnswer === index && (
              <span
                className={`text-sm font-medium ${
                  exercise.correctAnswer === index ? 'text-green-600' : 'text-red-600'
                }`}
              >
                {exercise.correctAnswer === index ? 'Correct' : 'Incorrect'}
              </span>
            )}
          </label>
        ))}
      </div>

      {showFeedback && selectedAnswer !== null && exercise.explanation && (
        <div
          className={`p-4 rounded-lg border ${
            exercise.correctAnswer === selectedAnswer
              ? 'bg-green-50 border-green-200'
              : 'bg-red-50 border-red-200'
          }`}
        >
          <div className="flex items-start gap-2">
            {exercise.correctAnswer === selectedAnswer ? (
              <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
            ) : (
              <X className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
            )}
            <div>
              <p
                className={`text-sm font-medium mb-1 ${
                  exercise.correctAnswer === selectedAnswer ? 'text-green-800' : 'text-red-800'
                }`}
              >
                Explanation:
              </p>
              <p
                className={`text-sm ${
                  exercise.correctAnswer === selectedAnswer ? 'text-green-700' : 'text-red-700'
                }`}
              >
                {exercise.explanation}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
